import { Button, Card, CardContent } from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { BackgroundVideo } from '@/shared/ui/BackgroundVideo';
import { ScopedPermission } from '@/shared/ui';
import type { ProjectSnapshotProps } from './ProjectSnapshot.types';
import { useProjectSnapshot } from './useProjectSnapshot';

export function ProjectSnapshot(props: ProjectSnapshotProps) {
  const vm = useProjectSnapshot(props);

  // The "Create" CTA is an onboarding nudge, shown only while the project has
  // no briefs yet. `hasBriefs === undefined` (loading, error or unknown) hides
  // it to avoid flicker and accidental clicks.
  const showCreateCta = props.hasBriefs === false && props.onCreateBrief !== undefined;

  // No heading: the project header already names the section, and the card
  // leads with the project's own words.
  return (
    <Card compact className="h-full gap-0 p-0 shadow-none!">
      <CardContent className="flex flex-1 flex-col p-0">
        <div className="relative flex flex-1 flex-col justify-between overflow-hidden rounded-lg">
          <div className="pointer-events-none absolute inset-0 z-1 bg-linear-to-b from-transparent to-black/40" />

          <BackgroundVideo
            src={vm.videoSrc}
            fallback={vm.videoFallbackSrc}
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div className="relative z-10 flex flex-col gap-md p-4">
            {/* The design's 34px step is not on the ramp; `type-heading-xl` is the
                nearest rung and already carries its weight and letter-spacing. */}
            <h3 className="type-heading-xl text-white">{vm.heading}</h3>
          </div>

          {showCreateCta && (
            <div
              data-testid="project-snapshot-create-cta"
              className="relative z-10 flex items-center justify-between gap-2.5 bg-white/10 px-4 py-2 backdrop-blur-[14px]"
            >
              <p className="text-sm text-white">{vm.ctaText}</p>
              <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
                {/* Tessra has no light-on-media variant. The outline hover layers a
                    translucent tint and a grey border, which reads as broken over the
                    video, so hover and press keep the white border and only dim the fill. */}
                <Button
                  variant="outline"
                  size="default"
                  className="shrink-0 border-white bg-white text-black hover:border-white hover:bg-white/90 hover:bg-none active:border-white active:bg-white/90 active:bg-none"
                  onClick={vm.onCreateBrief}
                >
                  <PlusIcon className="size-4" />
                  {vm.ctaLabel}
                </Button>
              </ScopedPermission>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
